import {
  ZOOM_CALL_CONNECTED_TYPE,
  ZOOM_CHANNEL_AWARE_EVENT_TYPES,
  ZOOM_CHANNEL_TO_CATEGORY,
  ZOOM_EMBED_EXPAND_EVENT,
  ZOOM_EMBED_IFRAME_ID,
  ZOOM_EMBED_MESSAGE_TYPES,
  ZOOM_ENGAGEMENT_EVENT_TYPES,
  ZOOM_ENGAGEMENT_LOG_CATEGORIES,
  ZOOM_PHONE_CALL_LOG_TYPE
} from '@/constants/zoomEmbed'
import { Logger } from '@/utils/logger'

const zoomLogger = Logger.create('ZoomEmbed')

const ENGAGEMENT_TYPE_TO_CATEGORY = Object.entries(ZOOM_ENGAGEMENT_EVENT_TYPES)
  .reduce((acc, [category, types]) => {
    types.forEach((type) => {
      acc[type] = category
    })
    return acc
  }, {})

const CHANNEL_AWARE_TYPES = new Set(ZOOM_CHANNEL_AWARE_EVENT_TYPES)

const CATEGORY_LABELS = {
  [ZOOM_ENGAGEMENT_LOG_CATEGORIES.INBOUND_CALL]: 'โทรเข้า',
  [ZOOM_ENGAGEMENT_LOG_CATEGORIES.INBOUND_CHAT]: 'chat เข้า',
  [ZOOM_ENGAGEMENT_LOG_CATEGORIES.INBOUND_EMAIL]: 'email เข้า',
  [ZOOM_ENGAGEMENT_LOG_CATEGORIES.INBOUND_OMNI]: 'omni เข้า',
  [ZOOM_ENGAGEMENT_LOG_CATEGORIES.OUTBOUND_CALL]: 'โทรออก'
}

export const ZOOM_DIAL_STATUS = {
  READY: 'ready',
  NOT_CONFIGURED: 'not_configured',
  NOT_READY: 'not_ready',
  INVALID_PHONE: 'invalid_phone'
}

export function isZoomEmbedOrigin (origin) {
  try {
    const { hostname } = new URL(origin)
    return hostname === 'zoom.us' || hostname.endsWith('.zoom.us')
  } catch {
    return false
  }
}

export function buildZoomEmbedUrl () {
  const configured = process.env.VUE_APP_ZOOM_EMBED_URL || ''
  if (!configured) return ''

  const appOrigin = (process.env.VUE_APP_URL || window.location.origin).replace(/\/$/, '')

  try {
    const url = new URL(configured)
    url.searchParams.set('origin', appOrigin)
    return url.toString()
  } catch {
    return configured
  }
}

/**
 * Soft Embed readiness for click-to-call (config + iframe mounted).
 * @returns {'ready'|'not_configured'|'not_ready'}
 */
export function getZoomSoftEmbedStatus () {
  if (!process.env.VUE_APP_ZOOM_EMBED_URL) {
    return ZOOM_DIAL_STATUS.NOT_CONFIGURED
  }

  const iframe = document.getElementById(ZOOM_EMBED_IFRAME_ID)
  if (!iframe?.contentWindow || !iframe.src) {
    return ZOOM_DIAL_STATUS.NOT_READY
  }

  try {
    // Validate iframe src before postMessage targetOrigin
    const { origin } = new URL(iframe.src)
    if (!origin) return ZOOM_DIAL_STATUS.NOT_READY
    return ZOOM_DIAL_STATUS.READY
  } catch {
    return ZOOM_DIAL_STATUS.NOT_READY
  }
}

/**
 * Start an outbound Zoom Contact Center call through Soft Embed.
 * @param {string|number} phone
 * @returns {{ ok: boolean, status: string }}
 */
export function dialZoomPhone (phone) {
  const normalizedPhone = String(phone ?? '').trim()
  if (!normalizedPhone) {
    return { ok: false, status: ZOOM_DIAL_STATUS.INVALID_PHONE }
  }

  const status = getZoomSoftEmbedStatus()
  if (status !== ZOOM_DIAL_STATUS.READY) {
    return { ok: false, status }
  }

  const iframe = document.getElementById(ZOOM_EMBED_IFRAME_ID)
  const targetOrigin = new URL(iframe.src).origin

  window.dispatchEvent(new CustomEvent(ZOOM_EMBED_EXPAND_EVENT))
  iframe.contentWindow.postMessage({
    type: ZOOM_EMBED_MESSAGE_TYPES.CLICK_TO_CALL,
    data: {
      phone: normalizedPhone
    }
  }, targetOrigin)

  return { ok: true, status: ZOOM_DIAL_STATUS.READY }
}

/**
 * Parse Zoom CC inbound call-connected postMessage (agent answered a call).
 * @param {MessageEvent} event
 * @param {{ skipOriginCheck?: boolean }} [options]
 * @returns {{ ani: string, skillName: string, engagementId: string }|null}
 */
export function parseZoomInboundCallConnected (event, options = {}) {
  if (!options.skipOriginCheck && !isZoomEmbedOrigin(event.origin)) return null

  const { type, data } = event.data || {}
  if (type !== ZOOM_CALL_CONNECTED_TYPE || !data) return null
  if (String(data.direction || '').toLowerCase() !== 'inbound') return null

  const ani = String(data.from || '').trim()
  if (!ani) return null

  return {
    ani,
    skillName: String(data.queueName || '').trim(),
    engagementId: String(data.engagementId || '').trim()
  }
}

/**
 * Parse Zoom Smart Embed resize postMessage.
 * @param {MessageEvent} event
 * @param {{ skipOriginCheck?: boolean }} [options]
 * @returns {{ width: number, height: number }|null}
 */
export function parseZoomEmbedResize (event, options = {}) {
  if (!options.skipOriginCheck && !isZoomEmbedOrigin(event.origin)) return null

  const { type, data } = event.data || {}
  if (type !== ZOOM_EMBED_MESSAGE_TYPES.RESIZE || !data) return null

  const width = Number(data.widthInPixels)
  const height = Number(data.heightInPixels)

  if (!Number.isFinite(width) || !Number.isFinite(height)) return null

  return { width, height }
}

/**
 * Resolve category from channel / channelType fields on Zoom payloads.
 * @param {object|null} data
 * @returns {string|null}
 */
function categoryFromChannel (data) {
  if (!data || typeof data !== 'object') return null

  const raw = data.channelType ?? data.channel ?? data.engagementChannel ?? ''
  const channel = String(raw).toLowerCase().trim()
  if (!channel) return null

  return ZOOM_CHANNEL_TO_CATEGORY[channel] || null
}

/**
 * Resolve category from zcc-phone-call-log payload.
 * @param {object|null} data
 * @returns {string|null}
 */
function categoryFromCallLog (data) {
  const record = data?.objectRecord || {}
  const engagementType = String(record.type || '').toLowerCase()
  const callType = String(record.callType || '').toLowerCase()

  const fromType = ZOOM_CHANNEL_TO_CATEGORY[engagementType]
  if (fromType && engagementType !== 'voice' && engagementType !== 'phone') {
    return fromType
  }

  if (callType === 'outbound') {
    return ZOOM_ENGAGEMENT_LOG_CATEGORIES.OUTBOUND_CALL
  }
  if (callType === 'inbound' || engagementType === 'phone' || engagementType === 'voice') {
    return ZOOM_ENGAGEMENT_LOG_CATEGORIES.INBOUND_CALL
  }

  return categoryFromChannel(data) || fromType || null
}

/**
 * Resolve engagement category for a Zoom CC postMessage type.
 * Prefer payload channel when present (chat / email / omni / voice).
 * @param {string} type
 * @param {object|null} [data]
 * @returns {string|null}
 */
export function getZoomEngagementCategory (type, data = null) {
  if (!type || typeof type !== 'string') return null

  if (type === ZOOM_PHONE_CALL_LOG_TYPE) {
    return categoryFromCallLog(data)
  }

  if (CHANNEL_AWARE_TYPES.has(type)) {
    const fromChannel = categoryFromChannel(data)
    if (fromChannel) return fromChannel
    // Email-named contact event without channel → treat as email
    if (type === 'zcc-incomingEmail-request') {
      return ZOOM_ENGAGEMENT_LOG_CATEGORIES.INBOUND_EMAIL
    }
  }

  return ENGAGEMENT_TYPE_TO_CATEGORY[type] || categoryFromChannel(data) || null
}

/**
 * Log Zoom CC engagement events (call / chat / email / omni / outbound).
 * Observation only — no side effects.
 * @param {MessageEvent} event
 * @param {{ skipOriginCheck?: boolean }} [options]
 */
export function logZoomEmbedEngagement (event, options = {}) {
  if (!options.skipOriginCheck && !isZoomEmbedOrigin(event.origin)) return

  const payload = event.data
  if (!payload || typeof payload !== 'object') return

  const { type } = payload
  if (!type || typeof type !== 'string' || !type.startsWith('zcc-')) return

  // Skip noisy resize in engagement logs
  if (type === ZOOM_EMBED_MESSAGE_TYPES.RESIZE) return

  const data = payload.data ?? null
  const category = getZoomEngagementCategory(type, data)
  const channel = data?.channelType ?? data?.channel ?? data?.objectRecord?.type ?? null

  if (category) {
    zoomLogger.info(CATEGORY_LABELS[category] || category, {
      category,
      type,
      channel,
      data,
      origin: event.origin
    })
    return
  }

  // Other zcc-* events — keep for discovery while observing
  zoomLogger.debug('zcc event', { type, channel, data, origin: event.origin })
}
